<template>
  <div class="flex gap-2">
    <component
      :is="as"
      v-for="input in inputs"
      v-model="input.value"
      ref="inputs"
      :key="input.key"
      :name="input.key === 'day' ? name : null"
      :placeholder="input.placeholder"
      :maxlength="input.length"
      type="text"
      inputmode="numeric"
      :inputClass="inputClass"
      :ariaLabel="input.key === 'day' ? 'Jour' : input.key === 'month' ? 'Mois' : 'Année'"
      @compositionstart="onCompositionStart"
      @beforeinput="onBeforeinput"
      @keydown.arrow-up.prevent="onArrow(input, 'up')"
      @keydown.arrow-down.prevent="onArrow(input, 'down')"
      @input="onInput($event, input)"
      @blur="onBlur(input)"
      @update:model-value="onUpdate"
    />
  </div>
</template>

<script>
export default defineNuxtComponent({
  emits: ['update:model-value', 'blur'],
  props: {
    modelValue: { type: [String, Date], default: '' },
    name: { type: String, default: null },
    variant: {
      type: String,
      default: 'default',
      validator: (v) => ['dsfr', 'default'].includes(v),
    },
    inputClass: { type: [String, Array], default: null },
    required: { type: Boolean, default: false }, // TODO
  },
  data() {
    return {
      inputs: [
        {
          key: 'day',
          length: 2,
          max: 31,
          placeholder: 'JJ',
          ref: 'dayInput',
          value: '',
        },
        {
          key: 'month',
          length: 2,
          max: 12,
          placeholder: 'MM',
          ref: 'monthInput',
          value: '',
        },
        {
          key: 'year',
          length: 4,
          max: 9999,
          defaultValue: new Date().getFullYear(),
          placeholder: 'AAAA',
          ref: 'yearInput',
          value: '',
        },
      ],
    }
  },
  watch: {
    modelValue: {
      immediate: true,
      handler(newValue) {
        if (!newValue) {
          return
        }
        const [year, month, day] = newValue.split('-')
        this.year = year
        this.month = month
        this.day = day
      },
    },
  },
  computed: {
    year: {
      get() {
        return this.inputs.find((s) => s.key === 'year').value
      },
      set(val) {
        this.inputs.find((s) => s.key === 'year').value = val
      },
    },
    month: {
      get() {
        return this.inputs.find((s) => s.key === 'month').value
      },
      set(val) {
        this.inputs.find((s) => s.key === 'month').value = val
      },
    },
    day: {
      get() {
        return this.inputs.find((s) => s.key === 'day').value
      },
      set(val) {
        this.inputs.find((s) => s.key === 'day').value = val
      },
    },
    as() {
      return this.variant === 'dsfr' ? resolveComponent('DsfrInput') : resolveComponent('BaseInput')
    },
  },
  methods: {
    onCompositionStart(event) {
      // Hack, since we cannot prevent an insertCompositionText event
      // https://w3c.github.io/input-events/#interface-InputEvent-Attributes
      event.target.blur()
      setTimeout(() => event.target.focus(), 0)
    },
    onBeforeinput(event) {
      // insertText, insertFromPaste, etc.
      // https://w3c.github.io/input-events/#interface-InputEvent-Attributes
      if (event.inputType.startsWith('insert')) {
        // Only digits
        if (!/^\d+$/.test(event.data)) {
          event.preventDefault()
        }
      }
    },
    onArrow(input, direction) {
      let value

      if (!this[input.key]) {
        value = input.defaultValue ?? (direction === 'down' ? input.max : 1)
      } else {
        value = parseInt(this[input.key], 10) + (direction === 'down' ? -1 : 1)
        if (direction === 'up' && value > input.max) {
          value = 1
        } else if (direction === 'down' && value < 1) {
          value = input.max
        }
      }

      this[input.key] = value.toString().padStart(input.length, '0')
      this.onUpdate()
    },
    onInput(event, input) {
      if (!(event instanceof InputEvent)) {
        return
      }

      // Focus next input
      if (event.inputType.startsWith('insert')) {
        if (input.value.length === input.length && ['day', 'month'].includes(input.key)) {
          const nextInput =
            input.key === 'day'
              ? this.$refs.inputs[1].$refs.input
              : this.$refs.inputs[2].$refs.input
          this.focusInput(nextInput)
        }
      }
    },
    onUpdate() {
      this.$emit('update:model-value', `${this.year}-${this.month}-${this.day}`)
    },
    onBlur(input) {
      if (this[input.key] && this[input.key].length < input.length) {
        this[input.key] = this[input.key].padStart(input.length, '0')
        this.onUpdate()
      }
      this.checkActiveElement()
    },
    async checkActiveElement() {
      // Emit blur event if the active element is not one of the inputs
      await new Promise((resolve) => setTimeout(resolve, 25))
      const inputs = this.$refs.inputs.map((i) => i.$refs.input)
      if (!inputs.includes(document.activeElement)) {
        this.$emit('blur')
      }
    },
    focusInput(elInput) {
      elInput?.focus()
      elInput?.setSelectionRange(0, elInput.value.length)
    },
  },
})
</script>
